import { useCallback } from 'react';
import { EAttributeType } from '../../../@types/AttributeTypes';
import { IBuildingProviderContext } from '../../../@types/ContextProviderRelated';
import { EOpeningDirection, EOrientation } from '../../../@types/Enums';
import { EHistoryAction } from '../../../hooks/useUndoRedo';
import { IWall } from '../../../models/abstract/Wall';
import AttributeModel from '../../../models/standard/AttributeModel';
import DoorModel from '../../../models/standard/DoorModel';
import PartitionWallModel from '../../../models/standard/PartitionWallModel';
import StandardWallModel from '../../../models/standard/StandardWallModel';
import { DefaultAttributeId, getDefaultAttributesForObject, ObjectType } from '../../../utils/attributes/defaultObjectsAndAttributes';

export interface IDoorActions {
  deleteDoor: (wallIndex: number, doorIndex: number) => void;
  updateDoorWidth: (wallIndex: number, doorIndex: number, newWidth: number) => void;
  updateDoorOpeningDirection: (wallIndex: number, doorIndex: number) => void;
  updateDoorOrientation: (wallIndex: number, doorIndex: number) => void;
  updateDoorAttributes: (wallIndex: number, doorIndex: number, attributes: AttributeModel[]) => void;
  setDoorWidthAndOpeningDirAttributes: (wallIndex: number, doorIndex: number, width: number, widthUnit: string | null, openingDirection: string) => void;
  getGermanDoorDirectionText: (openingDirection: EOpeningDirection, orientation: EOrientation) => string;
}

export const useDoorActions = (setBuilding: React.Dispatch<React.SetStateAction<IBuildingProviderContext>>): IDoorActions => {
  
  // Helper to reconstruct a wall with updated doors while preserving other properties
  const recreateWallWithDoors = (originalWall: IWall, newDoors: DoorModel[]): IWall | null => {
    if (originalWall instanceof StandardWallModel) {
      const radsA = [...originalWall.sides.A.radiators];
      const radsB = [...originalWall.sides.B.radiators];

      const newWall = new StandardWallModel(
        originalWall.startPoint,
        originalWall.endPoint,
        originalWall.depth,
        originalWall.windows,
        newDoors,
        [], // Legacy radiators (empty)
        originalWall.attributes,
        originalWall.uuid,
        originalWall.passages,
      );

      // Restore radiators
      newWall.sides.A.radiators = radsA;
      newWall.sides.B.radiators = radsB;
      newWall.updateWallLengths();
      return newWall;
    } 
    
    if (originalWall instanceof PartitionWallModel) {
      // Reconstruct PartitionWall safely
      const newWall = new PartitionWallModel(originalWall.startPoint, originalWall.endPoint);
      newWall.depth = originalWall.depth;
      newWall.windows = originalWall.windows;
      newWall.doors = newDoors;
      newWall.passages = originalWall.passages;
      newWall.sides = originalWall.sides; // Preserve radiators/sides
      newWall.attributes = originalWall.attributes;
      newWall.setUUID(originalWall.uuid);
      
      if (newWall.updateWallLengths) {
        newWall.updateWallLengths();
      }
      return newWall;
    }

    return null;
  };

  const deleteDoor = useCallback(
    (wallIndex: number, doorIndex: number): void => {
      setBuilding((prev) => {
        const walls = [...prev.walls]; // Use generic IWall array
        const currentWall = walls[wallIndex];

        if (!currentWall || !currentWall.doors) return prev;

        const doors = [...currentWall.doors];
        doors.splice(doorIndex, 1);

        const newWall = recreateWallWithDoors(currentWall, doors);

        if (newWall) {
          walls[wallIndex] = newWall;
          return { ...prev, walls, isSetByUndoRedo: false, lastAction: EHistoryAction.DOOR_DELETED };
        }
        return prev;
      });
    },
    [setBuilding],
  );

  const updateDoorWidth = useCallback(
    (wallIndex: number, doorIndex: number, newWidth: number): void => {
      setBuilding((prev) => {
        const walls = [...prev.walls];
        const currentWall = walls[wallIndex];

        if (!currentWall || !currentWall.doors) return prev;

        const doors = [...currentWall.doors];
        const d = doors[doorIndex];
        const newDoor = new DoorModel(d.position, newWidth, d.depth, d.angle, d.openingDirection, d.orientation, d.attributes);
        newDoor.setUUID(d.uuid);
        doors[doorIndex] = newDoor;

        const newWall = recreateWallWithDoors(currentWall, doors);

        if (newWall) {
          walls[wallIndex] = newWall;
          return { ...prev, walls, isSetByUndoRedo: false, lastAction: EHistoryAction.DOOR_WIDTH_CHANGED };
        }
        return prev;
      });
    },
    [setBuilding],
  );

  const updateDoorOpeningDirection = useCallback(
    (wallIndex: number, doorIndex: number): void => {
      setBuilding((prev) => {
        const walls = [...prev.walls];
        const currentWall = walls[wallIndex];

        if (!currentWall || !currentWall.doors) return prev;

        const doors = [...currentWall.doors];
        const d = doors[doorIndex];
        const newDir = d.openingDirection === EOpeningDirection.RIGHT ? EOpeningDirection.LEFT : EOpeningDirection.RIGHT;
        const newDoor = new DoorModel(d.position, d.length, d.depth, d.angle, newDir, d.orientation, d.attributes);
        newDoor.setUUID(d.uuid);
        doors[doorIndex] = newDoor;

        const newWall = recreateWallWithDoors(currentWall, doors);

        if (newWall) {
          walls[wallIndex] = newWall;
          return { ...prev, walls, isSetByUndoRedo: false, lastAction: EHistoryAction.DOOR_OPENING_DIRECTION_CHANGED };
        }
        return prev;
      });
    },
    [setBuilding],
  );

  const updateDoorOrientation = useCallback(
    (wallIndex: number, doorIndex: number): void => {
      setBuilding((prev) => {
        const walls = [...prev.walls];
        const currentWall = walls[wallIndex];

        if (!currentWall || !currentWall.doors) return prev;

        const doors = [...currentWall.doors];
        const d = doors[doorIndex];
        const newOrient = d.orientation === EOrientation.INWARD ? EOrientation.OUTWARD : EOrientation.INWARD;
        const newDoor = new DoorModel(d.position, d.length, d.depth, d.angle, d.openingDirection, newOrient, d.attributes);
        newDoor.setUUID(d.uuid);
        doors[doorIndex] = newDoor;

        const newWall = recreateWallWithDoors(currentWall, doors);

        if (newWall) {
          walls[wallIndex] = newWall;
          return { ...prev, walls, isSetByUndoRedo: false, lastAction: EHistoryAction.DOOR_ORIENTATION_CHANGED };
        }
        return prev;
      });
    },
    [setBuilding],
  );

  const updateDoorAttributes = useCallback(
    (wallIndex: number, doorIndex: number, attributes: AttributeModel[]): void => {
      setBuilding((prev) => {
        const walls = [...prev.walls];
        const currentWall = walls[wallIndex];

        if (!currentWall || !currentWall.doors) return prev;

        const doors = [...currentWall.doors];
        const d = doors[doorIndex];
        const defaultDoorAttrs = getDefaultAttributesForObject(ObjectType.Door);
          
        const updatedAttrs = attributes.map((attr) => {
          const defaultAttr = defaultDoorAttrs.find((def) => def.id === attr.id);
          return new AttributeModel(
            attr.id,
            attr.key,
            attr.value,
            attr.predecessorId,
            attr.type,
            attr.unit,
            attr.hidden ?? defaultAttr?.hidden ?? false,
            attr.forced ?? defaultAttr?.forced ?? false,
            attr.ownerId,
            attr.userId,
            attr.publicFlag,
            attr.localization,
            attr.defaults,
            attr.validation,
            attr.unitFamily
          );
        });
        
        const newDoor = new DoorModel(d.position, d.length, d.depth, d.angle, d.openingDirection, d.orientation, updatedAttrs);
        newDoor.setUUID(d.uuid);
        doors[doorIndex] = newDoor;

        const newWall = recreateWallWithDoors(currentWall, doors);

        if (newWall) {
          walls[wallIndex] = newWall;
          return { ...prev, walls, isSetByUndoRedo: false, lastAction: EHistoryAction.DOOR_ATTRIBUTES_UPDATED };
        }
        return prev;
      });
    },
    [setBuilding],
  );

  const setDoorWidthAndOpeningDirAttributes = useCallback(
    (wallIndex: number, doorIndex: number, width: number, widthUnit: string | null, openingDirection: string): void => {
      setBuilding((prev) => {
        const walls = [...prev.walls];
        const currentWall = walls[wallIndex];

        if (!currentWall || !currentWall.doors) return prev;

        const doors = [...currentWall.doors];
        const currentDoor = doors[doorIndex];

        if (!currentDoor.attributes || currentDoor.attributes.length === 0) return prev;

        const updatedAttrs = currentDoor.attributes.map((attr) => {
          if (attr.id === DefaultAttributeId.DOOR_WIDTH) {
            return new AttributeModel(
              attr.id, attr.key, width, attr.predecessorId, EAttributeType.NUMBER, widthUnit, attr.hidden, attr.forced,
              attr.ownerId, attr.userId, attr.publicFlag, attr.localization, attr.defaults, attr.validation, attr.unitFamily
            );
          }
          if (attr.id === DefaultAttributeId.DOOR_OPENING_DIRECTION) {
            return new AttributeModel(
              attr.id, attr.key, openingDirection, attr.predecessorId, EAttributeType.TEXT, null, attr.hidden, attr.forced,
              attr.ownerId, attr.userId, attr.publicFlag, attr.localization, attr.defaults, attr.validation, attr.unitFamily
            );
          }
          return attr;
        });

        const newDoor = new DoorModel(
          currentDoor.position,
          currentDoor.length,
          currentDoor.depth,
          currentDoor.angle,
          currentDoor.openingDirection,
          currentDoor.orientation,
          updatedAttrs,
        );
        newDoor.setUUID(currentDoor.uuid);
        doors[doorIndex] = newDoor;

        const newWall = recreateWallWithDoors(currentWall, doors);

        if (newWall) {
          walls[wallIndex] = newWall;
          return { ...prev, walls, isSetByUndoRedo: false, lastAction: EHistoryAction.DOOR_WIDTH_AND_DIRECTION_ATTRIBUTES_UPDATED };
        }
        return prev;
      });
    },
    [setBuilding],
  );

  function getGermanDoorDirectionText(openingDirection: EOpeningDirection, orientation: EOrientation): string {
    const dinText = openingDirection === EOpeningDirection.LEFT ? 'rechts' : 'links';
    const orientText = orientation === EOrientation.INWARD ? 'nach innen' : 'nach außen';
    return `${dinText}, ${orientText}`;
  }

  return {
    deleteDoor,
    updateDoorWidth,
    updateDoorOpeningDirection,
    updateDoorOrientation,
    updateDoorAttributes,
    setDoorWidthAndOpeningDirAttributes,
    getGermanDoorDirectionText,
  };
};
